import { useState, useContext, useMemo } from 'react'
import { Typography, InputAdornment, TextField, Box, Tabs, Tab, Button, Stack, Switch } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import moment from 'moment-timezone'
import { enqueueSnackbar } from 'notistack'

import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import { AppContext } from '/providers/app-context'
import { changeGiftcardBalance, toggleScannerPassEnabled, setScannerPassExpiry } from '/providers/studio-api'
import StyledPaper from '/components/paper'

const PassEditorGift = ({ pass, template, setPass }) => {

  const [tabIndex, setTabIndex] = useState("pay")

  return (
    <div className='h-full pb-32'>
      <StyledPaper>
        <Typography variant="h6" sx={{ p: 2 }}>Giftcard Editor (£{pass.scanner_instance_data["__*_amount__"].toFixed(2)})</Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 5 }}>
          <Tabs value={tabIndex} onChange={(e, value) => setTabIndex(value)}>
            <Tab label="Payment" value="pay" />
            <Tab label="Refund" value="refund" />
            <Tab label="Top-up" value="credit" />
            <Tab label="Manage" value="manage" />
          </Tabs>
        </Box>
      </StyledPaper>

      <div className='overflow-y-scroll flex flex-col h-full pb-32 items-center justify-around'>
        <StyledPaper>
          <div className='p-5'>
            <PayTab tabIndex={tabIndex} index="pay" pass={pass} templateId={template.id} setPass={setPass} />
            <RefundTab tabIndex={tabIndex} index="refund" pass={pass} templateId={template.id} setPass={setPass} />
            <CreditTab tabIndex={tabIndex} index="credit" pass={pass} templateId={template.id} setPass={setPass} />
            <ManageTab tabIndex={tabIndex} index="manage" pass={pass} templateId={template.id} setPass={setPass} />
          </div>
        </StyledPaper>
      </div>
    </div>
  )
}

export default PassEditorGift

const PayTab = ({ tabIndex, index, pass, templateId, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)
  const [amount, setAmount] = useState("")

  const save = async () => {
    setProcessing(true)
    const result = await changeGiftcardBalance({ apiSource, apiKey, templateId, passId: pass.pass_id, action: "debit", amount: parseFloat(amount) })
    setProcessing(false)

    if (result.ok == false) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setAmount("")
      setPass(result)
      enqueueSnackbar({ message: 'Giftcard debited', variant: 'success' })
    }
  }

  if (tabIndex === index) return <NumericInput pass={pass} amount={amount} setAmount={setAmount} label="Payment amount" helperText="Process a payment using the Giftcard" submitText="Pay" save={save} />
}

const RefundTab = ({ tabIndex, index, pass, templateId, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)
  const [amount, setAmount] = useState("")

  const save = async () => {
    setProcessing(true)
    const result = await changeGiftcardBalance({ apiSource, apiKey, templateId, passId: pass.pass_id, action: "refund", amount: parseFloat(amount) })
    setProcessing(false)

    if (result.ok == false) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setAmount("")
      setPass(result)
      enqueueSnackbar({ message: 'Giftcard refunded', variant: 'success' })
    }
  }

  if (tabIndex === index) return <NumericInput pass={pass} amount={amount} setAmount={setAmount} label="Refund amount" helperText="Refund a previous payment back to the Giftcard." submitText="Refund" save={save} />
}

const CreditTab = ({ tabIndex, index, pass, templateId, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)
  const [amount, setAmount] = useState("")

  const save = async () => {
    setProcessing(true)
    const result = await changeGiftcardBalance({ apiSource, apiKey, templateId, passId: pass.pass_id, action: "credit", amount: parseFloat(amount) })
    setProcessing(false)

    if (result.ok == false) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setAmount("")
      setPass(result)
      enqueueSnackbar({ message: 'Giftcard topped-up', variant: 'success' })
    }
  }

  if (tabIndex === index) return <NumericInput pass={pass} amount={amount} setAmount={setAmount} label="Top-up amount" helperText="Use this to add additional funds to a Giftcard." submitText="Top-up" save={save} />
}

const regex = /^(\d+(?:[\.]\d{0,2})?)$/i

const NumericInput = ({ pass, amount, setAmount, label, helperText, submitText, save }) => {

  const expired = useMemo(() => pass.expiry_date && pass.expiry_date < moment().format("YYYYMMDDHHmmSS"), [pass.expiry_date])
  const disabled = pass.scanner_instance_data["*_disabled"] !== null

  return (
    <div className='flex flex-1 flex-col justify-around items-center'>
      <div>
        <div className='flex flex-col justify-around items-center gap-10'>
          <div className='font-semibold text-3xl self-start mb-5 text-gray-400'>Balance £{pass.scanner_instance_data["__*_balance__"].toFixed(2)}</div>

          {!expired && !disabled &&
            <>

              <TextField
                value={amount}
                onChange={e => {
                  if (e.target.value === '' || regex.test(e.target.value)) {
                    setAmount(e.target.value)
                  }
                }}
                variant='outlined'
                label={label}
                placeholder='0.00'
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start"><div style={{ fontSize: 50, color: "#999" }}>£</div></InputAdornment>,
                    style: { fontSize: 50, width: 300 }
                  },
                  inputLabel: {
                    shrink: true,
                    style: { fontSize: 30 }
                  }
                }}
              />

              <div className='font-semibold text-xl self-start'>{helperText}</div>
              <div className='self-end'><Button variant='contained' disabled={amount === ""} onClick={save}>{submitText}</Button></div>
            </>
          }

          {disabled &&
            <div><WarningAmberIcon className='mr-5 text-red-500' />
              Giftcard is disabled and cannot be used
              <br />
              <span className='text-gray-500'>You can change this in the MANAGE section</span>
            </div>
          }

          {expired &&
            <div><WarningAmberIcon className='mr-5 text-red-500' />
              Giftcard has expired and cannot be used
              <br />
              <span className='text-gray-500'>You can change this in the MANAGE section</span>
            </div>
          }

        </div>
      </div>
    </div>
  )
}


const ManageTab = ({ tabIndex, index, pass, templateId, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)

  const [expiry, setExpiry] = useState(moment(pass.expiry_date, "YYYYMMDDHHmmSS"))
  const [enabled, setEnabled] = useState(pass.scanner_instance_data["*_disabled"] === null)
  const [note, setNote] = useState("")

  const passCurrentlyEnabled = useMemo(() => pass.scanner_instance_data["*_disabled"] === null, [pass.scanner_instance_data["*_disabled"]])

  const dateDisabled = useMemo(() => expiry.format("YYYYMMDDHHmmSS") === pass.expiry_date, [expiry, pass.expiry_date])
  const noteDisabled = useMemo(() => enabled === passCurrentlyEnabled, [enabled, passCurrentlyEnabled])

  const now = moment()

  const saveEnabledDisabled = async () => {
    setProcessing(true)
    const result = await toggleScannerPassEnabled({ apiSource, apiKey, templateId, passId: pass.pass_id, enabled, note })
    setProcessing(false)

    if (result.error) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setPass(result)
      setNote("")
      enqueueSnackbar({ message: 'Giftcard ' + (enabled ? "enabled" : "disabled"), variant: 'success' })
    }
  }

  const saveExpiryDate = async () => {
    setProcessing(true)
    const result = await setScannerPassExpiry({ apiSource, apiKey, templateId, passId: pass.pass_id, expiry: expiry.format("YYYYMMDDHHmmSS") })
    setProcessing(false)

    if (result.error) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setPass(result)
      enqueueSnackbar({ message: 'Giftcard expiry date changed', variant: 'success' })
    }
  }

  if (tabIndex === index) return (
    <div className='flex flex-1 flex-col justify-around items-center'>
      <div>
        <div className='flex flex-col justify-around items-center gap-10'>

          <div className='mt-5 flex flex-row gap-5 items-center'>
            <DatePicker label="Expiry Date" value={expiry} onChange={date => setExpiry(date)} minDate={now} format='DD/MM/YYYY' />
            <Button variant='contained' disabled={dateDisabled} onClick={saveExpiryDate}>Save</Button>
          </div>
          {pass.mode === "test" && <div className='italic text-gray-300'>NOTE: TEST Giftcards cannot be extended beyond 24 hours after creation.</div>}

          <div style={{ width: 400 }} className='flex flex-col gap-5'>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography>Giftcard status: Disabled</Typography>
              <Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />
              <Typography>Enabled</Typography>
            </Stack>

            <TextField multiline fullWidth rows={4} label="Note" onChange={e => setNote(e.target.value)} disabled={noteDisabled} helperText={`Add a note to explain why you are ${passCurrentlyEnabled ? "disabling" : "enabling"} this giftcard `} slotProps={{
              inputLabel: {
                shrink: true,
              }
            }}>{note}</TextField>
            <div className='self-end'>
              <Button variant='contained' disabled={noteDisabled} onClick={saveEnabledDisabled}>Save</Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}


const formatDate = date => moment(date, "YYYYMMDDHHmmSS").format("DD MMM YYYY HH:mm")